import React, { useEffect, useRef, useState } from "react";
import api from "../../utils/api";
import { useToast } from "../../contexts/ToastContext";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import SharedBarcodePicker from "../SharedBarcodePicker";
import {
  addOrIncrementCartEntry,
  buildScannedCartOption,
} from "../../utils/cashierStock";
import {
  buildReceiptCorrectionPayload,
  calculateReceiptTotal,
  normalizeReceiptLine,
} from "../../utils/receiptHistory";

const formatCurrency = (value) =>
  Math.round(Number(value) || 0).toLocaleString("en-US");

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

const getFocusableElements = (container) =>
  Array.from(
    container?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ) || [],
  );

function containDialogFocus(event, container, onClose) {
  if (event.key === "Escape") {
    event.preventDefault();
    onClose();
    return;
  }
  if (event.key !== "Tab") return;

  const focusable = getFocusableElements(container);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first) {
    event.preventDefault();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

const normalizeInitialLines = (items) =>
  (Array.isArray(items) ? items : []).map(normalizeReceiptLine);

export default function ReceiptCorrectionDialog({ receipt, onClose, onSaved }) {
  const showToast = useToast();
  const showConfirmation = useConfirmation();
  const dialogRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const returnFocusRef = useRef(null);
  const [lines, setLines] = useState(() => normalizeInitialLines(receipt?.items));
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [barcode, setBarcode] = useState("");
  const [sharedBarcodeChoice, setSharedBarcodeChoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    dialogRef.current?.querySelector("button, input")?.focus();
    return () => returnFocusRef.current?.focus?.();
  }, []);

  useEffect(() => {
    setLines(normalizeInitialLines(receipt?.items));
  }, [receipt]);

  const updateLine = (index, field, value) => {
    setLines((current) =>
      current.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [field]: value } : line,
      ),
    );
  };

  const removeLine = (index) => {
    setLines((current) => current.filter((_, lineIndex) => lineIndex !== index));
  };

  const addOption = (option) => {
    const scannedOption = buildScannedCartOption(option);
    setLines((current) => addOrIncrementCartEntry(current, scannedOption));
    setSharedBarcodeChoice(null);
    setBarcode("");
    setShowAddProduct(false);
    setTimeout(() => barcodeInputRef.current?.focus(), 100);
  };

  const scanBarcode = async (event) => {
    if (event?.key && event.key !== "Enter") return;
    event?.preventDefault();
    const value = barcode.trim();
    if (!value || scanning) return;

    setScanning(true);
    try {
      const response = await api.get(`/sales/scan/${encodeURIComponent(value)}`);
      const item = response.data;
      if (item?.requiresChoice) {
        setSharedBarcodeChoice({
          barcode: value,
          options: (item.options || []).map(buildScannedCartOption),
        });
      } else {
        addOption(item);
      }
    } catch (error) {
      showToast(errorMessage(error, "Product could not be found."), "error");
    } finally {
      setScanning(false);
    }
  };

  const persistCorrection = async (payload) => {
    setSaving(true);
    try {
      const response = await api.patch(
        `/receipts/${encodeURIComponent(receipt.transactionId)}`,
        {
          items: payload,
          reason: "Cashier corrected checkout mistake",
        },
      );
      showToast("Receipt corrected and stock updated.", "success");
      onSaved(response.data);
    } catch (error) {
      showToast(errorMessage(error, "Receipt could not be corrected."), "error");
    } finally {
      setSaving(false);
    }
  };

  const saveCorrection = () => {
    let payload;
    try {
      payload = buildReceiptCorrectionPayload(lines.map(normalizeReceiptLine));
    } catch (error) {
      showToast(error.message, "error");
      return;
    }

    showConfirmation(
      "Save receipt correction?",
      "This will update the receipt lines, total, and stock history.",
      () => {
        void persistCorrection(payload);
      },
    );
  };

  if (!receipt) return null;

  return (
    <div className="modal-overlay receipt-correction-overlay">
      <div
        ref={dialogRef}
        className="modal modal-lg receipt-correction-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="receipt-correction-title"
        onKeyDown={(event) =>
          containDialogFocus(event, dialogRef.current, onClose)
        }
      >
        <div className="modal-header">
          <div>
            <p className="eyebrow">TODAY’S RECEIPT</p>
            <h2 id="receipt-correction-title">Correct {receipt.transactionId}</h2>
            <p>Change products, quantities, or selling prices before saving.</p>
          </div>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Close receipt correction"
          >
            &times;
          </button>
        </div>

        <div className="receipt-correction-body">
          <div className="receipt-correction-lines">
            {lines.length === 0 ? (
              <div className="receipt-correction-empty">
                <strong>Add at least one product</strong>
                <p>A corrected receipt cannot be saved without a product.</p>
              </div>
            ) : (
              lines.map((line, index) => (
                <article
                  className="receipt-correction-line"
                  data-correction-line={line.name}
                  key={`${line.unitId || line.itemId || line.barcode}-${index}`}
                >
                  <div className="receipt-correction-line-heading">
                    <div>
                      <strong>{line.name}</strong>
                      <span>
                        {line.barcode || "No barcode"} · {line.unitName || "Unit"}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="table-row-action danger-action"
                      data-remove-line={line.name}
                      onClick={() => removeLine(index)}
                      aria-label={`Remove ${line.name}`}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="receipt-correction-line-fields">
                    <label>
                      <span>Quantity</span>
                      <input
                        aria-label={`Quantity for ${line.name}`}
                        type="number"
                        min="1"
                        step="1"
                        value={line.quantity}
                        onChange={(event) =>
                          updateLine(index, "quantity", event.target.value)
                        }
                      />
                    </label>
                    <label>
                      <span>Price per {line.unitName || "unit"} (MMK)</span>
                      <input
                        aria-label={`Price for ${line.name}`}
                        type="number"
                        min="0"
                        step="1"
                        value={line.unitPrice}
                        onChange={(event) =>
                          updateLine(index, "unitPrice", event.target.value)
                        }
                      />
                    </label>
                    <div className="receipt-correction-line-total">
                      <span>Line total</span>
                      <strong>
                        MMK {formatCurrency(Number(line.quantity) * Number(line.unitPrice))}
                      </strong>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>

          <section className="receipt-correction-add" aria-label="Add product">
            <button
              type="button"
              className="btn btn-secondary"
              data-toggle-add-product
              aria-expanded={showAddProduct}
              onClick={() => {
                setShowAddProduct((current) => !current);
                setTimeout(() => barcodeInputRef.current?.focus(), 0);
              }}
            >
              {showAddProduct ? "Hide barcode entry" : "Add by barcode"}
            </button>
            {showAddProduct && (
              <div className="receipt-correction-barcode-entry">
                <label>
                  <span>Scan barcode to add a product</span>
                  <input
                    ref={barcodeInputRef}
                    aria-label="Barcode to add"
                    type="text"
                    value={barcode}
                    onChange={(event) => setBarcode(event.target.value)}
                    onKeyDown={scanBarcode}
                    placeholder="Scan or type barcode, then press Enter"
                    autoComplete="off"
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={scanBarcode}
                  disabled={scanning || !barcode.trim()}
                >
                  {scanning ? "Looking up..." : "Add product"}
                </button>
              </div>
            )}
          </section>
        </div>

        <div className="modal-footer receipt-correction-footer">
          <div className="receipt-correction-total">
            <span>Corrected total</span>
            <strong>MMK {formatCurrency(calculateReceiptTotal(lines.map(normalizeReceiptLine)))}</strong>
          </div>
          <div className="receipt-correction-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              data-save-correction
              onClick={saveCorrection}
              disabled={saving || lines.length === 0}
            >
              {saving ? "Saving..." : "Save correction"}
            </button>
          </div>
        </div>
      </div>

      {sharedBarcodeChoice && (
        <SharedBarcodePicker
          barcode={sharedBarcodeChoice.barcode}
          options={sharedBarcodeChoice.options}
          onSelect={addOption}
          onCancel={() => setSharedBarcodeChoice(null)}
          title="Choose the scanned product"
          description="This barcode belongs to more than one product unit."
        />
      )}
    </div>
  );
}
